import React, { useState, useRef, useEffect } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import Wrapper from '@components/Wrapper';
import Logo from '@components/Logo';
import LogoDark from '@components/LogoDark';
import ProgressBar from '@components/blog/ProgressBar';
import RightClickLogoMenu from '@components/RightClickLogoMenu';
import menuDropdownIconBlack from '@icons/menuDropdownIconBlack';
import codeIcon from '@icons/codeIcon';
import copyIcon from '@icons/copyIcon';
import downloadIcon from '@icons/downloadIcon';
import dfLogo from '@icons/dealfront';

import { useI18next, useTranslation } from 'gatsby-plugin-react-i18next';
import { BurgerButton } from './BurgerButton';
import NavButtons from './NavButtons';
import { NavMenu } from './NavMenu';

import * as styles from './style.module.scss';

const {
  topNavigation,
  topNavigationFixed,
  wrapper,
  hasNavVisible,
  logo,
  topNavigation__nav,
  topNavigation__bgtransparent,
  topNavigationNotScrolled,
  topNavigationScrolled,
  preventTransition,
  subMenuOpened,
  topNavigation__backButton,
  topNavigation__mobilecta,
} = styles;

const TopNavigation = ({
  navType,
  progressBar,
  LogoComponent,
  transparent = false,
  greenButton = false,
  classes = '',
}) => {
  const { path } = useI18next();
  const { t } = useTranslation();
  const data = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
        }
      }
    }
  `);
  const isUsPage = path.startsWith('/us/');

  const isWindowDefined = typeof window !== 'undefined';

  const [isDesktop, setIsDesktop] = useState(
    isWindowDefined && window.matchMedia('(min-width: 1440px)').matches
  );
  const [isNavOpened, setIsNavOpened] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDisabledTransition, setIsDisabledTransition] = useState(false);
  const [isSubMenuOpened, setIsSubMenuOpened] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const headerRef = useRef(0);
  const navMenuRef = useRef();

  const handleBurgerClick = () => {
    setIsDisabledTransition(true);
    setIsNavOpened(!isNavOpened);
  };

  const handleScroll = () => {
    setIsDisabledTransition(false);
    if (window.pageYOffset === 0) {
      setIsScrolled(false);
    } else if (window.pageYOffset !== 0 && !isScrolled) {
      setIsScrolled(true);
    }
  };
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 1140;
      setIsMobileView(isMobile);

      if (!isMobile) {
        setIsNavOpened(false);
        setIsSubMenuOpened(false);
      }

      // Optional: update isScrolled as well
      if (window.pageYOffset === 0) {
        setIsScrolled(false);
      } else {
        setIsScrolled(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <header
      className={`${topNavigation} ${
        isDisabledTransition ? preventTransition : ''
      } ${navType === 'fixed' ? topNavigationFixed : ''} 
      ${isSubMenuOpened ? subMenuOpened : ''} ${
        !isScrolled && (!isNavOpened || isDesktop)
          ? topNavigationNotScrolled
          : topNavigationScrolled
      } ${
        transparent === true
          ? topNavigation__bgtransparent
          : greenButton === true
          ? `background--dark-purple`
          : 'background--white'
      } ${isNavOpened ? hasNavVisible : ''}`}
      ref={headerRef}
    >
      {progressBar && <ProgressBar />}
      <Wrapper classes={wrapper}>
        {LogoComponent ? (
          isMobileView && isSubMenuOpened ? (
            <button
              onClick={() => {
                navMenuRef.current?.goBackToRoot?.();
                setIsSubMenuOpened(false);
              }}
              className={topNavigation__backButton}
            >
              Back
            </button>
          ) : (
            LogoComponent
          )
        ) : isMobileView && isSubMenuOpened ? (
          <button
            onClick={() => {
              navMenuRef.current?.goBackToRoot?.();
              setIsSubMenuOpened(false);
            }}
            className={topNavigation__backButton}
          >
            Back
          </button>
        ) : (
          <RightClickLogoMenu
            menuItems={[
              {
                label: (
                  <>
                    <span className="c-right-click-logo__menu-button-text">
                      Copy logo as SVG
                    </span>
                    <span className="c-right-click-logo__menu-button-text--copying">
                      Copying…
                    </span>
                  </>
                ),
                type: 'copy',
                link: '/images/dealfront-logo.svg',
                icon: codeIcon,
              },
              {
                label: (
                  <>
                    <span className="c-right-click-logo__menu-button-text">
                      Copy logo as PNG
                    </span>
                    <span className="c-right-click-logo__menu-button-text--copying">
                      Copying…
                    </span>
                  </>
                ),
                type: 'copy',
                link: '/images/dealfront-logo.png',
                icon: copyIcon,
              },
              {
                label: (
                  <>
                    <span className="c-right-click-logo__menu-button-text">
                      Download brand kit
                    </span>
                    <span className="c-right-click-logo__menu-button-text--copying">
                      Downloading…
                    </span>
                  </>
                ),
                link: ' https://marketing.dealfront.com/assets/dealfront-logo.zip',
                icon: downloadIcon,
              },
            ]}
          >
            <Logo
              href={`${isUsPage ? '/us' : '/'}`}
              type={isNavOpened ? 'nav' : 'nav--closed'}
              classes={`${logo}`}
              title={t('nav_home_page')}
            />
          </RightClickLogoMenu>
        )}
        <>
          <div className={topNavigation__nav}>
            <div className={`wrapper ${wrapper}`}>
              <NavMenu
                ref={navMenuRef}
                headerRef={headerRef}
                isNavOpened={isNavOpened}
                isSubMenuOpened={isSubMenuOpened}
                setIsSubMenuOpened={setIsSubMenuOpened}
                isScrolled={isScrolled}
              />
              <NavButtons
                isMobileView={isMobileView}
                isSubMenuOpened={isSubMenuOpened}
                isScrolled={isScrolled}
                greenButton={greenButton}
              />
            </div>
          </div>
          <BurgerButton
            greenButton={greenButton}
            isNavOpened={isNavOpened}
            onClick={handleBurgerClick}
            withMobileCta={true}
          />
        </>
      </Wrapper>
    </header>
  );
};

export default TopNavigation;
